import { setCookieValue } from '@do/seashell';
import { useSearchParams } from '@do/seashell/navigation';
import { useEffect } from 'react';
import { ACTIVATION_REDIRECT_COOKIE, ACTIVATION_REDIRECT_PARAM } from '../constants';

const ACCEPTED_DOMAIN = '.digitalocean.com';

/**
 * Checks if there is an "activation_redirect" query param
 * If the redirect url is valid and:
 *  is a relative url, it sets a cookie with the relative url
 *  is an absolute url, it sets a cookie with the full url
 * if the redirect url is not a valid url, it clears the query param and logs an error
 *
 * @returns the value of the query param
 */

const useActivationRedirectCookie = (): string | null => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activationRedirectUrl = searchParams.get(ACTIVATION_REDIRECT_PARAM);
  const isRelativeUrl = activationRedirectUrl && activationRedirectUrl.startsWith('/');

  useEffect(() => {
    if (!activationRedirectUrl) return;

    if (isRelativeUrl) {
      setCookieValue(ACTIVATION_REDIRECT_COOKIE, activationRedirectUrl);
      return;
    }

    try {
      const url = new URL(activationRedirectUrl);

      if (url.hostname.endsWith(ACCEPTED_DOMAIN)) {
        setCookieValue(ACTIVATION_REDIRECT_COOKIE, activationRedirectUrl);
        return;
      }

      // clear url params if the domain is not accepted
      searchParams.delete(ACTIVATION_REDIRECT_PARAM);
      setSearchParams(searchParams);
      console.error('Unauthorized redirection attempt');
    } catch (e) {
      console.error('Invalid URL', e);
    }
  }, [activationRedirectUrl, isRelativeUrl, searchParams, setSearchParams]);

  return activationRedirectUrl;
};

export default useActivationRedirectCookie;
